import { ACTIVITY_POINTS, type ActivityDto, type ActivityKind } from '@spin/shared';
import { useEffect, useState } from 'react';
import { http } from '../api/http';
import { Dialog } from '../components/Dialog';
import { Emoji } from '../components/Emoji';
import { Avatar, Spinner } from '../components/common';
import { reportError, useT } from '../i18n';
import { useSession } from '../store/session';

/** The points table shows these first; "More" shows the rest. */
const FIRST: ActivityKind[] = ['message', 'song', 'wheel', 'booster'];

function useClock(): number {
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    const id = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(id);
  }, []);
  return now;
}

const pad = (n: number) => String(n).padStart(2, '0');

/**
 * "Bonus & Rating" (the gift icon in the message bar): today's active play time with its rewards, how points are
 * earned, and the daily top 20 (yesterday's too).
 */
export function BonusDialog() {
  const t = useT();
  const me = useSession((s) => s.me)!;
  const [day, setDay] = useState<'today' | 'yesterday'>('today');
  const [more, setMore] = useState(false);
  const [loaded, setLoaded] = useState<{ day: string; value: ActivityDto } | null>(null);
  const data = loaded?.day === day ? loaded.value : null;
  const now = useClock();

  useEffect(() => {
    http
      .get<ActivityDto>(`/activity?day=${day}`)
      .then((value) => setLoaded({ day, value }))
      .catch(reportError);
  }, [day]);

  const kinds = (Object.keys(ACTIVITY_POINTS) as ActivityKind[]).sort(
    (a, b) => (FIRST.indexOf(a) + 1 || 99) - (FIRST.indexOf(b) + 1 || 99),
  );
  const shownKinds = more ? kinds : kinds.filter((k) => FIRST.includes(k));
  const left = data ? Math.max(0, data.endsAt - now) : 0;

  return (
    <Dialog title={t('activity.title')} className="bonus-dialog" testId="bonus">
      {!data ? (
        <Spinner />
      ) : (
        <>
          {day === 'today' && (
            <div className="bonus-time" data-testid="play-time">
              <span className="bonus-fire" aria-hidden>
                <Emoji e="🔥" />
              </span>
              <div>
                <small>⏱ {t('activity.time').toUpperCase()}</small>
                <b>
                  {pad(Math.floor(data.playMinutes / 60))}:{pad(data.playMinutes % 60)}:00
                </b>
              </div>
              <div className="bonus-milestones">
                {data.milestones.map((m) => (
                  <span key={m.hours} className={m.reached ? 'reached' : ''}>
                    {t('activity.hours', { hours: m.hours })}
                    <small>❤{m.reward}</small>
                    <i>
                      <em style={{ width: `${Math.min(100, (data.playMinutes / (m.hours * 60)) * 100)}%` }} />
                    </i>
                  </span>
                ))}
              </div>
            </div>
          )}
          <div className="bonus-section">{t('activity.how').toUpperCase()}</div>
          <div className="bonus-points">
            {shownKinds.map((k) => (
              <span key={k}>
                {t(`activity.kind.${k}`)}
                <b>+{ACTIVITY_POINTS[k]}</b>
              </span>
            ))}
            <button className="bonus-more" onClick={() => setMore((m) => !m)}>
              {more ? t('activity.less') : t('activity.more')} {more ? '▴' : '▾'}
            </button>
          </div>
          <div className="bonus-top-head">
            <b>TOP 20</b>
            {day === 'today' && (
              <span className="bonus-timer">
                ⏳ {Math.floor(left / 3_600_000)}
                {t('activity.h')} {pad(Math.floor((left % 3_600_000) / 60_000))}
                {t('activity.m')}
              </span>
            )}
            <button className="bonus-day" onClick={() => setDay(day === 'today' ? 'yesterday' : 'today')}>
              {day === 'today' ? t('activity.yesterday') : t('activity.today')} ↺
            </button>
          </div>
          {data.top.length === 0 ? (
            <p className="bonus-empty">{t('activity.empty')}</p>
          ) : (
            <div className="bonus-top" data-testid="activity-top">
              {data.top.map((e) => (
                <div
                  key={e.userId}
                  className={['bonus-row', e.rank <= 3 && 'podium', e.userId === me.id && 'mine']
                    .filter(Boolean)
                    .join(' ')}
                >
                  <span className="bonus-rank">{e.rank}</span>
                  <Avatar name={e.displayName} photoUrl={e.photoUrl} size={24} />
                  <span className="ellipsis">{e.displayName}</span>
                  <b>{t('activity.pts', { points: e.points })}</b>
                </div>
              ))}
            </div>
          )}
          {data.me && (
            <p className="bonus-me">
              {t('activity.my_place', { rank: data.me.rank, points: data.me.points })}
            </p>
          )}
        </>
      )}
    </Dialog>
  );
}
